/**
 * Step 3/4 — visual usage per AI-09 §C1 (P1 decision 5: color usage
 * tags + free-text typography guidance only; logo placement is Phase 2
 * — no image generation to consume it).
 */
import { useTranslation } from "react-i18next";

import type { BrandKitColors } from "../../../../lib/brandKits";
import type { StepProps } from "./ToneStep";

const USAGE_TAGS = ["ctas", "headlines", "accents", "body"] as const;

export function VisualStep({
  draft,
  update,
  colors,
}: StepProps & { colors: BrandKitColors | undefined }): JSX.Element {
  const { t } = useTranslation();

  const colorEntries = Object.entries(colors ?? {}).filter(
    (pair): pair is [string, string] => typeof pair[1] === "string" && pair[1] !== "",
  );
  const usage = draft.color_usage ?? {};

  function toggle(colorKey: string, tag: string): void {
    const current = usage[colorKey] ?? [];
    const next = current.includes(tag)
      ? current.filter((v) => v !== tag)
      : [...current, tag];
    const nextUsage = { ...usage };
    if (next.length === 0) {
      delete nextUsage[colorKey];
    } else {
      nextUsage[colorKey] = next;
    }
    update({ color_usage: nextUsage });
  }

  return (
    <div className="space-y-5">
      <div>
        <p className="mb-2 text-sm text-neutral-600">{t("brandVoice.visual.colors_question")}</p>
        {colorEntries.length === 0 ? (
          <p className="rounded-md bg-neutral-50 px-3 py-2 text-sm text-neutral-500">
            {t("brandVoice.visual.no_colors")}
          </p>
        ) : (
          <ul className="space-y-2">
            {colorEntries.map(([key, hex]) => (
              <li key={key} className="flex flex-wrap items-center gap-3 rounded-md border border-neutral-200 p-2.5">
                <span className="flex items-center gap-2 text-sm font-medium text-neutral-800">
                  <span
                    aria-hidden="true"
                    className="inline-block h-4 w-4 rounded-full border border-neutral-300"
                    style={{ backgroundColor: hex }}
                  />
                  {key}
                  <span className="font-normal text-neutral-400">{hex}</span>
                </span>
                <span className="flex flex-wrap gap-2">
                  {USAGE_TAGS.map((tag) => (
                    <label key={tag} className="inline-flex items-center gap-1 text-sm text-neutral-700">
                      <input
                        type="checkbox"
                        checked={(usage[key] ?? []).includes(tag)}
                        onChange={() => toggle(key, tag)}
                      />
                      {t(`brandVoice.visual.tags.${tag}`)}
                    </label>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <label htmlFor="voice-typography" className="block text-sm font-medium text-neutral-700">
          {t("brandVoice.visual.typography_label")}
        </label>
        <input
          id="voice-typography"
          type="text"
          maxLength={500}
          value={draft.typography_guidance ?? ""}
          placeholder={t("brandVoice.visual.typography_placeholder")}
          onChange={(e) => update({ typography_guidance: e.target.value })}
          className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </div>
    </div>
  );
}
